import React from 'react';
import { Checkbox, Popover } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { BUILDING_MIN_ZOOM, LEVEL_COLORS, MIN_LOAD_ZOOM, SOURCE_GROUP_LABELS, SourceGroup } from '../constants';
import { SourceVisibility } from '../types';

interface Props {
    visibility: SourceVisibility;
    onToggle: (group: SourceGroup) => void;
    /** 当前地图 zoom; 用于说明"这一级现在为什么不显示"。 */
    zoom: number | null;
    /**
     * 「未生效」图层开关 (V2.1 人工审核): 默认关, 地图与从前完全一致;
     * 打开后瓦片请求带 includeUnverified=true, 待审核的面以虚线画出来。
     */
    showUnverified: boolean;
    onToggleUnverified: () => void;
}

const GROUPS: SourceGroup[] = ['MANUAL', 'SURVEY', 'OVERTURE'];

/** 色块: 与地图上面的描边/填充同色, 帮用户把图例和面对应起来。 */
function swatch(hex: string, rgb: [number, number, number]): React.CSSProperties {
    return {
        width: 12,
        height: 12,
        borderRadius: 2,
        background: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},.28)`,
        border: `1.5px solid ${hex}`,
        flex: 'none',
        display: 'inline-block',
    };
}

/**
 * 地图左下角图例。
 *
 * <p>两段分开表达, 避免"颜色"这一个通道被塞进两个含义:
 * <ul>
 *   <li>色卡 = 级别 (1级青 / 2级紫), 跟地图上面的颜色一一对应, 常驻;</li>
 *   <li>来源图层开关 (手绘 / 实采 / 导入) 默认收进「图层」按钮 (2026-08-04 需求),
 *       点开 Popover 再勾选; 有图层被隐藏时按钮显示橙色计数 —— 不然收起后更容易
 *       出现"自己关了手绘又忘了, 新画的面消失"的坑。</li>
 * </ul>
 */
/**
 * zoom 说明文案: 分级加载是刻意的 (低 zoom 一屏能盖住上万个楼栋), 但不说清楚就会被当成 bug ——
 * "2 级看得到、放大了 1 级还没出来"、"点建筑物没反应"(点的其实是底图房子, 不是 AOI 面)。
 */
function zoomHint(zoom: number | null): string | null {
    if (zoom == null) return null;
    if (zoom < MIN_LOAD_ZOOM) return `当前 zoom ${zoom}: 未加载 AOI, 放大到 ${MIN_LOAD_ZOOM} 开始加载`;
    if (zoom < BUILDING_MIN_ZOOM) return `当前 zoom ${zoom}: 只显示 2 级, 放大到 ${BUILDING_MIN_ZOOM} 显示 1 级楼栋`;
    return null;
}

/**
 * 未生效面的图例色块: 虚线描边 + <b>较重填充</b>, 与地图上 aoiTileLayer 的样式对应。
 *
 * <p>填充深浅必须跟着 styleFor 走: 未生效是待办, 在地图上比已生效更抢眼 (fillOpacity 0.35 vs 0.14),
 * 图例要是还画成极浅, 运营会以为自己看到的是另一种东西。
 */
const UNVERIFIED_SWATCH: React.CSSProperties = {
    width: 12,
    height: 12,
    borderRadius: 2,
    background: 'rgba(148,163,184,.35)',
    border: '2px dashed #64748b',
    flex: 'none',
    display: 'inline-block',
};

const Legend: React.FC<Props> = ({ visibility, onToggle, zoom, showUnverified, onToggleUnverified }) => {
    const hiddenCount = GROUPS.filter((g) => !visibility[g]).length;
    return (
        <div className="absolute flex flex-col items-start gap-2" style={{ bottom: 16, left: 12, zIndex: 1200 }}>
            {zoomHint(zoom) && (
                <div
                    className="rounded-lg bg-white px-3 py-1 text-xs shadow-md"
                    style={{ color: '#8c96a5' }}
                >
                    {zoomHint(zoom)}
                </div>
            )}
            <div
                className="flex items-center gap-4 rounded-lg bg-white px-3 py-2 text-xs shadow-md"
                style={{ color: 'rgba(0,0,0,.65)' }}
            >
                <span className="flex items-center gap-1.5">
                    <span style={swatch(LEVEL_COLORS.LEVEL_1.hex, LEVEL_COLORS.LEVEL_1.rgb)} />
                    1级
                </span>
                <span className="flex items-center gap-1.5">
                    <span style={swatch(LEVEL_COLORS.LEVEL_2.hex, LEVEL_COLORS.LEVEL_2.rgb)} />
                    2级
                </span>
                <span style={{ width: 1, height: 14, background: '#e5e9f0' }} />
                <Popover
                    trigger="click"
                    placement="topLeft"
                    content={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {GROUPS.map((g) => (
                                <Checkbox key={g} checked={visibility[g]} onChange={() => onToggle(g)}>
                                    <span className="text-xs">{SOURCE_GROUP_LABELS[g]}</span>
                                </Checkbox>
                            ))}
                            <span style={{ height: 1, background: '#e5e9f0' }} />
                            {/* 与来源开关语义相反: 来源是"关掉隐藏", 这个是"打开才显示", 默认不显示待审核面 */}
                            <Checkbox checked={showUnverified} onChange={onToggleUnverified}>
                                <span className="flex items-center gap-1.5 text-xs">
                                    <span style={UNVERIFIED_SWATCH} />
                                    未生效 (待审核)
                                </span>
                            </Checkbox>
                        </div>
                    }
                >
                    <button
                        type="button"
                        className="flex items-center gap-1"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            fontSize: 12,
                            cursor: 'pointer',
                            color: hiddenCount > 0 ? '#d48806' : 'rgba(0,0,0,.65)',
                        }}
                    >
                        图层{hiddenCount > 0 ? `（已隐藏 ${hiddenCount}）` : ''}{showUnverified ? '（含未生效）' : ''}
                        <DownOutlined style={{ fontSize: 9 }} />
                    </button>
                </Popover>
            </div>
        </div>
    );
};

export default Legend;
